import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { FileDown, Plus, Pencil, Trash2, BookOpen, Settings, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { confirmAsync } from '@/lib/confirm';

function FacturasRecibidas({ user }) {
  const navigate = useNavigate();
  const [facturas, setFacturas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFactura, setEditingFactura] = useState(null);
  const [formData, setFormData] = useState({
    numero_factura: '',
    fecha_emision: '',
    proveedor_id: '',
    proveedor_nombre: '',
    proveedor_nif: '',
    proveedor_direccion: '',
    items: [{ description: '', quantity: 1, price: 0, total: 0 }],
    subtotal: 0,
    tax: 0,
    total: 0,
    tipo_gasto: '623',
    concepto: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadFacturas();
    loadProveedores();
  }, []);

  const loadFacturas = async () => {
    try {
      const response = await axiosInstance.get('/contabilidad/facturas-recibidas');
      setFacturas(response.data.facturas || []);
    } catch (error) {
      console.error('Error cargando facturas:', error);
      toast.error('Error al cargar las facturas recibidas');
    } finally {
      setLoading(false);
    }
  };

  const loadProveedores = async () => {
    try {
      const response = await axiosInstance.get('/proveedores');
      setProveedores(response.data.proveedores || []);
    } catch (error) {
      console.error('Error cargando proveedores:', error);
      toast.error('Error al cargar los proveedores');
    }
  };

  const handleProveedorSelect = (proveedorId) => {
    if (!proveedorId) {
      setFormData({
        ...formData,
        proveedor_id: '',
        proveedor_nombre: '',
        proveedor_nif: '',
        proveedor_direccion: ''
      });
      return;
    }

    const proveedor = proveedores.find(p => p.id === proveedorId);
    if (proveedor) {
      setFormData({
        ...formData,
        proveedor_id: proveedor.id,
        proveedor_nombre: proveedor.nombre_comercial,
        proveedor_nif: proveedor.nif,
        proveedor_direccion: proveedor.direccion || '',
        tipo_gasto: proveedor.cuenta_contable || '623'
      });
      toast.success(`Proveedor "${proveedor.nombre_comercial}" seleccionado`);
    }
  };

  const handleOpenModal = (factura = null) => {
    if (factura) {
      setEditingFactura(factura);
      setFormData({
        ...factura,
        fecha_emision: factura.fecha_emision.split('T')[0]
      });
    } else {
      setEditingFactura(null);
      setFormData({
        numero_factura: '',
        fecha_emision: new Date().toISOString().split('T')[0],
        proveedor_id: '',
        proveedor_nombre: '',
        proveedor_nif: '',
        proveedor_direccion: '',
        items: [{ description: '', quantity: 1, price: 0, total: 0 }],
        subtotal: 0,
        tax: 0,
        total: 0,
        tipo_gasto: '623',
        concepto: ''
      });
    }
    setShowModal(true);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'price') {
      newItems[index].total = newItems[index].quantity * newItems[index].price;
    }
    
    setFormData({ ...formData, items: newItems });
    calculateTotals(newItems);
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, price: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
    calculateTotals(newItems);
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const tax = subtotal * 0.21;
    const total = subtotal + tax;
    
    setFormData(prev => ({
      ...prev,
      subtotal: Math.round(subtotal * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      total: Math.round(total * 100) / 100
    }));
  };

  const handleSave = async () => {
    if (!formData.numero_factura || !formData.proveedor_nombre) {
      toast.error('El número de factura y nombre del proveedor son obligatorios');
      return;
    }

    setSaving(true);
    try {
      if (editingFactura) {
        await axiosInstance.put(`/contabilidad/facturas-recibidas/${editingFactura.id}`, formData);
        toast.success('Factura actualizada correctamente');
      } else {
        await axiosInstance.post('/contabilidad/facturas-recibidas', formData);
        toast.success('Factura creada correctamente');
      }
      setShowModal(false);
      loadFacturas();
    } catch (error) {
      console.error('Error guardando factura:', error);
      toast.error('Error al guardar la factura');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (facturaId) => {
    if (!(await confirmAsync('¿Estás seguro de eliminar esta factura?'))) return;

    try {
      await axiosInstance.delete(`/contabilidad/facturas-recibidas/${facturaId}`);
      toast.success('Factura eliminada correctamente');
      loadFacturas();
    } catch (error) {
      console.error('Error eliminando factura:', error);
      toast.error('Error al eliminar la factura');
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy', { locale: es });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando facturas...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <FileDown className="h-8 w-8 text-green-600" />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900">
                Contabilidad C10
              </h1>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/contabilidad/fiscal')}
                variant="outline"
                size="sm"
              >
                Dashboard
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/asientos')}
                variant="outline"
                size="sm"
              >
                Asientos
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/libros')}
                variant="outline"
                size="sm"
              >
                Libros
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/ocr')}
                variant="outline"
                size="sm"
              >
                OCR
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/config')}
                variant="outline"
                size="sm"
              >
                <Settings className="h-4 w-4" />
              </Button>
              <div className="border-l border-gray-300 mx-2"></div>
              <Button
                onClick={() => handleOpenModal()}
              >
                <Plus className="h-4 w-4 mr-2" />
                <span>Nueva Factura</span>
              </Button>
            </div>
          </div>
          <p className="text-base text-gray-600">
            Facturas Recibidas - Gestión de facturas de proveedores
          </p>
        </div>

        {facturas.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileDown className="h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No hay facturas recibidas
              </h3>
              <p className="text-gray-500 text-center max-w-md mb-4">
                Agrega las facturas que recibes de tus proveedores.
              </p>
              <Button onClick={() => handleOpenModal()}>
                <Plus className="h-4 w-4 mr-2" />
                Crear Primera Factura
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {facturas.map((factura) => (
              <Card key={factura.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-3">
                        <span>{factura.numero_factura}</span>
                        {factura.pagada && <Badge className="bg-green-100 text-green-800">Pagada</Badge>}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        <div className="text-sm space-y-1">
                          <div><strong>Proveedor:</strong> {factura.proveedor_nombre}</div>
                          <div><strong>Fecha:</strong> {formatDate(factura.fecha_emision)}</div>
                          {factura.concepto && <div><strong>Concepto:</strong> {factura.concepto}</div>}
                        </div>
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Total</div>
                      <div className="text-xl font-semibold">{formatCurrency(factura.total)}</div>
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenModal(factura)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(factura.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}

        {/* Modal de crear/editar */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingFactura ? 'Editar' : 'Nueva'} Factura Recibida
              </DialogTitle>
              <DialogDescription>
                {editingFactura ? 'Modifica' : 'Agrega'} los datos de la factura del proveedor
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="numero_factura">Número de Factura *</Label>
                  <Input
                    id="numero_factura"
                    value={formData.numero_factura}
                    onChange={(e) => setFormData({ ...formData, numero_factura: e.target.value })}
                    placeholder="FRA-2025-001"
                  />
                </div>
                <div>
                  <Label htmlFor="fecha_emision">Fecha de Emisión *</Label>
                  <Input
                    id="fecha_emision"
                    type="date"
                    value={formData.fecha_emision}
                    onChange={(e) => setFormData({ ...formData, fecha_emision: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="proveedor_select">Seleccionar Proveedor</Label>
                <select
                  id="proveedor_select"
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.proveedor_id}
                  onChange={(e) => handleProveedorSelect(e.target.value)}
                >
                  <option value="">-- Selecciona un proveedor o introduce manualmente --</option>
                  {proveedores
                    .filter(p => p.estado === 'revisado')
                    .map(proveedor => (
                      <option key={proveedor.id} value={proveedor.id}>
                        {proveedor.nombre_comercial} - {proveedor.nif}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Solo se muestran proveedores revisados. Si no encuentras el proveedor, créalo en el módulo de Proveedores.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="proveedor_nombre">Nombre del Proveedor *</Label>
                  <Input
                    id="proveedor_nombre"
                    value={formData.proveedor_nombre}
                    onChange={(e) => setFormData({ ...formData, proveedor_nombre: e.target.value })}
                    placeholder="Empresa S.L."
                  />
                </div>
                <div>
                  <Label htmlFor="proveedor_nif">NIF del Proveedor</Label>
                  <Input
                    id="proveedor_nif"
                    value={formData.proveedor_nif}
                    onChange={(e) => setFormData({ ...formData, proveedor_nif: e.target.value })}
                    placeholder="B12345678"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="tipo_gasto">Tipo de Gasto (Cuenta Contable)</Label>
                <select
                  id="tipo_gasto"
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.tipo_gasto}
                  onChange={(e) => setFormData({ ...formData, tipo_gasto: e.target.value })}
                >
                  <option value="600">600 - Compras de mercaderías</option>
                  <option value="621">621 - Arrendamientos y cánones</option>
                  <option value="622">622 - Reparaciones y conservación</option>
                  <option value="623">623 - Servicios profesionales</option>
                  <option value="624">624 - Transportes</option>
                  <option value="628">628 - Suministros</option>
                  <option value="629">629 - Otros servicios</option>
                </select>
              </div>

              <div>
                <Label htmlFor="concepto">Concepto</Label>
                <Input
                  id="concepto"
                  value={formData.concepto}
                  onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
                  placeholder="Descripción breve"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Items</Label>
                  <Button size="sm" onClick={addItem}>
                    <Plus className="h-3 w-3 mr-1" />
                    Añadir Item
                  </Button>
                </div>

                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                    <div className="col-span-5">
                      <Input
                        placeholder="Descripción"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Cant."
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Precio"
                        value={item.price}
                        onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        value={item.total.toFixed(2)}
                        disabled
                      />
                    </div>
                    <div className="col-span-1">
                      {formData.items.length > 1 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeItem(index)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span className="font-semibold">{formatCurrency(formData.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>IVA (21%):</span>
                  <span className="font-semibold">{formatCurrency(formData.tax)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span>{formatCurrency(formData.total)}</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default FacturasRecibidas;
